import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getBlogPosts } from '../../contentful';
import BlogCard from './BlogCard';
import '../../styles/blog-overrides.scss';

const BlogList = ({ posts: externalPosts, featuredPostId }) => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(!externalPosts);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    if (externalPosts) {
      setPosts(externalPosts);
      setLoading(false);
    } else {
      fetchPosts();
    }
  }, [externalPosts]);

  const filterPosts = useCallback(() => {
    let filtered = posts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.description.toLowerCase().includes(query) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(post =>
        post.tags && selectedTags.some(tag => post.tags.includes(tag))
      );
    }

    // For the grid display, exclude the featured post (it's shown separately)
    const postsForGrid = filtered.filter(post => post.id !== featuredPostId);
    setFilteredPosts(postsForGrid);
  }, [posts, searchQuery, selectedTags, featuredPostId]);

  useEffect(() => {
    filterPosts();
  }, [filterPosts]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const blogPosts = await getBlogPosts();
      setPosts(blogPosts);
      
      // Extract all unique tags
      const tags = [...new Set(blogPosts.flatMap(post => post.tags || []))];
      setAllTags(tags);
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSearchResults(query.length > 0);
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setShowSearchResults(false);
  };

  const handleSearchResultClick = (post) => {
    setSearchQuery('');
    setShowSearchResults(false);
    // Navigate to the post (this will be handled by the Link in BlogCard)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-accent/20 border-t-accent" />
      </div>
    );
  }

  return (
    <div>
      {/* Enhanced Search and Filter Section */}
      <div className="mb-6 sm:mb-8">
        {/* Enhanced Search Bar */}
        <div className="blog-search relative mb-4 sm:mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search posts by title, content, or tags..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full px-3 sm:px-6 py-2 sm:py-4 pl-12 sm:pl-16 bg-slate-800/50 rounded-lg sm:rounded-2xl shadow-lg border border-accent/10 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 text-sm sm:text-base text-slate-100 placeholder-slate-500"
            />
            <svg
              className="absolute left-3 sm:left-6 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-6 sm:h-6 text-slate-500 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 sm:right-5 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-accent transition-colors p-1 rounded-full hover:bg-accent/10"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Enhanced Search Results Dropdown */}
          <AnimatePresence>
            {showSearchResults && searchQuery && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                className="absolute top-full left-0 right-0 mt-2 sm:mt-3 bg-slate-800 rounded-xl sm:rounded-2xl shadow-xl border border-accent/10 z-50 max-h-80 sm:max-h-96 overflow-y-auto"
              >
                {filteredPosts.slice(0, 5).map((post) => (
                  <div
                    key={post.id}
                    onClick={() => handleSearchResultClick(post)}
                    className="p-4 sm:p-5 hover:bg-accent/5 cursor-pointer border-b border-accent/10 last:border-b-0 transition-all duration-300"
                  >
                    <h4 className="font-semibold text-slate-100 mb-2 text-sm sm:text-lg">{post.title}</h4>
                    <p className="text-xs sm:text-sm text-slate-400 mb-3 leading-relaxed">{post.description}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-accent rounded-full flex items-center justify-center text-background-dark text-xs font-bold">
                          {post.author ? post.author.charAt(0).toUpperCase() : 'A'}
                        </div>
                        <span className="font-medium text-xs sm:text-sm">{post.author}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="text-xs">{new Date(post.publishedAt).toLocaleDateString()}</span>
                      </div>
                      {post.tags && post.tags.length > 0 && (
                        <span className="text-accent font-medium text-xs">{post.tags[0]}</span>
                      )}
                    </div>
                  </div>
                ))}
                {filteredPosts.length === 0 && (
                  <div className="p-6 sm:p-8 text-center text-slate-400">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                      </svg>
                    </div>
                    <h3 className="text-sm sm:text-lg font-semibold mb-2">No posts found</h3>
                    <p className="text-xs sm:text-sm">Try adjusting your search terms</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Enhanced Tag Filters */}
        {allTags.length > 0 && (
          <div className="blog-filter mb-4 sm:mb-6">
            <h4 className="text-xs sm:text-lg font-semibold text-slate-100 mb-2 sm:mb-4">Filter by Topics</h4>
            <div className="flex flex-wrap gap-1 sm:gap-3">
              {allTags.map((tag) => (
                <motion.button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-2 sm:px-5 py-1 sm:py-3 rounded-md sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 ${
                    selectedTags.includes(tag)
                      ? 'bg-accent text-background-dark shadow-lg'
                      : 'bg-slate-800/50 text-slate-300 border border-accent/10 hover:bg-accent/10 hover:border-accent/20'
                  }`}
                >
                  {tag}
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Enhanced Active Filters */}
        {(searchQuery || selectedTags.length > 0) && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap items-center gap-1 sm:gap-3 mb-4 sm:mb-6 p-2 sm:p-4 bg-accent/5 rounded-lg sm:rounded-2xl border border-accent/10"
          >
            <span className="text-xs sm:text-sm font-semibold text-slate-300">Active filters:</span>
            {searchQuery && (
              <span className="px-2 sm:px-4 py-1 sm:py-2 bg-accent/10 text-accent text-xs sm:text-sm rounded-md sm:rounded-xl font-medium flex items-center gap-1 sm:gap-2">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                "{searchQuery}"
              </span>
            )}
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="px-2 sm:px-4 py-1 sm:py-2 bg-accent/10 text-accent text-xs sm:text-sm rounded-md sm:rounded-xl font-medium flex items-center gap-1 sm:gap-2"
              >
                <span className="text-xs">🏷️</span>
                {tag}
                <button
                  onClick={() => handleTagToggle(tag)}
                  className="ml-1 hover:text-accent transition-colors"
                >
                  ×
                </button>
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs sm:text-sm text-slate-400 hover:text-accent underline font-medium transition-colors"
            >
              Clear all
            </button>
          </motion.div>
        )}

        {/* Enhanced Results Count */}
        <div className="text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6 p-2 sm:p-4 bg-slate-800/30 rounded-lg sm:rounded-xl">
          <span className="font-semibold">
            {filteredPosts.length === (posts.length - (featuredPostId ? 1 : 0))
              ? `Showing all ${posts.length} posts`
              : `Showing ${filteredPosts.length} of ${posts.length} posts`}
          </span>
          {filteredPosts.length !== (posts.length - (featuredPostId ? 1 : 0)) && (
            <span className="text-slate-500 ml-2">
              (filtered results)
            </span>
          )}
        </div>
      </div>

      {/* Enhanced Blog Posts Grid */}
      {filteredPosts.length > 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
        >
          {filteredPosts.map((post, index) => (
            <BlogCard key={post.id} post={post} index={index} />
          ))}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 sm:py-20"
        >
          <div className="text-slate-400">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-slate-300 mb-3 sm:mb-4">No additional posts found</h3>
            <p className="text-sm sm:text-base text-slate-400 mb-6 sm:mb-8 max-w-md mx-auto leading-relaxed">
              {searchQuery || selectedTags.length > 0
                ? "Try adjusting your search terms or filters to find what you're looking for."
                : "Check back soon for new content and updates."}
            </p>
            {(searchQuery || selectedTags.length > 0) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearAllFilters}
                className="px-6 sm:px-8 py-2 sm:py-3 bg-accent text-background-dark rounded-lg sm:rounded-xl font-semibold hover:bg-accent/90 hover:shadow-lg transition-all duration-300 text-sm sm:text-base"
              >
                Clear All Filters
              </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BlogList; 